/**
 * Emergency Booking Popup Notification
 * A persistent, pulsing notification that appears when emergency bookings are created
 * Features the barber head logo with animated rings and URGENT badge
 *
 * Enhanced with:
 * - Full booking details (date, time, client history)
 * - Reject with alternative time flow
 * - Barber assignment capability
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePinLock } from '../../contexts/PinLockContext';
import { Bell, X, Eye, Clock, DollarSign, Calendar, User, Check, Send } from 'lucide-react';
import { emergencyBookingService, EmergencyBookingRequest } from '../../services/emergencyBookingService';
import { clientDataService } from '../../services/clientDataService';
import { barberDataService } from '../../services/barberDataService';
import NexusSLIK from '../../systems/NexusSLIK';
import { ymd, parseYmd } from '../../shared/dateUtils';
import { parseTimeToMinutes } from '../../shared/availability';
import './EmergencyBookingPopupNotification.css';

interface EmergencyNotification {
  booking: EmergencyBookingRequest;
  cpiScore: number;
  dismissed: boolean;
}

interface ProposedAlternative {
  date: string;
  time: string;
  barberId: string;
  barberName: string;
  reason: string;
}

export const EmergencyBookingPopupNotification: React.FC = () => {
  const navigate = useNavigate();
  // BARBER-SPECIFIC ROUTING (Jose 2026-11-01): emergency joins the one-brain pattern — the popup shows
  // only where the TARGET barber is tapped in; an "any barber" request (no selected barber) stays shop-wide.
  const { currentBarber } = usePinLock();
  const [notifications, setNotifications] = useState<EmergencyNotification[]>([]);
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [lastCheckedCount, setLastCheckedCount] = useState(0);

  // Reject with alternative flow
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [availableBarbers, setAvailableBarbers] = useState<any[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [proposedAlternative, setProposedAlternative] = useState<ProposedAlternative>({
    date: '',
    time: '',
    barberId: '',
    barberName: '',
    reason: ''
  });

  // Load available barbers for assignment
  useEffect(() => {
    const barbers = barberDataService.getAllBarbers().filter(b => b.status === 'active');
    setAvailableBarbers(barbers);
  }, []);

  // Load and check for new emergency bookings
  const checkForNewBookings = useCallback(() => {
    const pendingBookings = emergencyBookingService.getPendingBookings();
    const unreadNotifications = emergencyBookingService.getStaffNotifications().filter(n => !n.read);

    // Only show notifications for NEW bookings (unread)
    const newNotifications: EmergencyNotification[] = [];

    const myId = (currentBarber as any)?.barberId || (currentBarber as any)?.id || null;
    pendingBookings.forEach(booking => {
      // Barber-specific like arrival/waitlist: a specific-barber emergency shows ONLY on that barber's
      // tapped-in device; an "any barber" request (no target) stays shop-wide.
      const target = (booking as any).selectedBarberId || (booking as any).barberId || '';
      if (target && myId && target !== myId) return;
      const notification = unreadNotifications.find(n => n.bookingId === booking.id);
      if (notification && !notification.read) {
        // Calculate CPI for this client
        let cpiScore = 50; // Default
        try {
          const client = clientDataService.getClientById(booking.clientId);
          if (client) {
            // Safely access optional properties with type assertion
            const clientAny = client as any;
            const cpiResult = NexusSLIK.CPI.calculateCPI({
              id: client.id,
              name: `${client.firstName} ${client.lastName}`,
              visits: client.totalVisits || 0,
              totalSpent: client.totalSpent || 0,
              lastVisit: client.lastVisit?.toString() || new Date().toISOString(),
              referrals: clientAny.referrals || 0,
              isModelClient: client.membershipType === 'model',
              membershipType: client.membershipType || 'regular',
              noShowCount: clientAny.noShowCount || 0,
              cancellationCount: clientAny.cancellationCount || 0
            });
            cpiScore = cpiResult.score;
          }
        } catch (e) {
          console.log('Could not calculate CPI for client');
        }

        newNotifications.push({
          booking,
          cpiScore,
          dismissed: false
        });
      }
    });

    // Only update if we have new notifications
    if (newNotifications.length > lastCheckedCount) {
      setNotifications(prev => {
        // Merge new notifications, avoiding duplicates
        const existingIds = prev.map(n => n.booking.id);
        const uniqueNew = newNotifications.filter(n => !existingIds.includes(n.booking.id));
        return [...prev.filter(n => !n.dismissed), ...uniqueNew];
      });
      setLastCheckedCount(newNotifications.length);
    }
  }, [lastCheckedCount, currentBarber]);

  // Check for new bookings on mount and subscribe to updates
  useEffect(() => {
    checkForNewBookings();

    // (Removed a 3s setInterval poll — Jose 2026-10-19: it was redundant with the real-time Firestore
    // subscription below and caused periodic main-thread jank on the iPad. Real-time updates cover it.)

    // Subscribe to booking service updates
    const unsubscribe = emergencyBookingService.subscribe(() => {
      checkForNewBookings();
    });

    // Listen for showEmergencyBookingPopup event from the notification badge
    const handleShowPopup = (event: CustomEvent) => {
      const booking = event.detail;
      console.log('Received showEmergencyBookingPopup event:', booking);
      if (booking) {
        // Calculate CPI and show client modal
        let cpiScore = 50;
        let clientData: any = null;

        try {
          const client = clientDataService.getClientById(booking.clientId);
          if (client) {
            const clientAny = client as any;
            const cpiResult = NexusSLIK.CPI.calculateCPI({
              id: client.id,
              name: `${client.firstName} ${client.lastName}`,
              visits: client.totalVisits || 0,
              totalSpent: client.totalSpent || 0,
              lastVisit: client.lastVisit?.toString() || new Date().toISOString(),
              referrals: clientAny.referrals || 0,
              isModelClient: client.membershipType === 'model',
              membershipType: client.membershipType || 'regular',
              noShowCount: clientAny.noShowCount || 0,
              cancellationCount: clientAny.cancellationCount || 0
            });
            cpiScore = cpiResult.score;
            clientData = client;
          }
        } catch (e) {
          console.error('Error calculating CPI:', e);
        }

        // Create client object from booking data if not found
        if (!clientData) {
          const nameParts = (booking.clientName || 'Unknown Client').split(' ');
          clientData = {
            id: booking.clientId,
            firstName: nameParts[0] || 'Unknown',
            lastName: nameParts.slice(1).join(' ') || '',
            phone: booking.clientPhone || '',
            email: booking.clientEmail || '',
            totalVisits: 0,
            totalSpent: 0
          };
        }

        setSelectedClient({
          ...clientData,
          emergencyBooking: booking,
          cpiScore
        });
        setShowClientModal(true);
      }
    };

    window.addEventListener('showEmergencyBookingPopup', handleShowPopup as EventListener);

    // Listen for showEmergencyRejectModal event from the notification badge (Counter button)
    const handleShowRejectModal = (event: CustomEvent) => {
      const booking = event.detail;
      console.log('Received showEmergencyRejectModal event:', booking);
      if (booking) {
        // Set up the proposed alternative with the booking info
        setProposedAlternative({
          date: '',
          time: '',
          barberId: '',
          barberName: '',
          reason: ''
        });

        // Store the booking to reject
        const nameParts = (booking.clientName || 'Unknown Client').split(' ');
        setSelectedClient({
          id: booking.clientId,
          firstName: nameParts[0] || 'Unknown',
          lastName: nameParts.slice(1).join(' ') || '',
          phone: booking.clientPhone || '',
          email: booking.clientEmail || '',
          emergencyBooking: booking,
          cpiScore: 50
        });

        // Show the reject modal
        setShowRejectModal(true);
      }
    };

    window.addEventListener('showEmergencyRejectModal', handleShowRejectModal as EventListener);

    return () => {
      unsubscribe();
      window.removeEventListener('showEmergencyBookingPopup', handleShowPopup as EventListener);
      window.removeEventListener('showEmergencyRejectModal', handleShowRejectModal as EventListener);
    };
  }, [checkForNewBookings]);

  // Handle View Details - open client profile
  const handleViewDetails = (notification: EmergencyNotification) => {
    // Mark booking as seen (removes from notification queue)
    emergencyBookingService.markBookingAsSeen(notification.booking.id);

    // Get client data and show modal
    const client = clientDataService.getClientById(notification.booking.clientId);
    if (client) {
      setSelectedClient({
        ...client,
        emergencyBooking: notification.booking,
        cpiScore: notification.cpiScore
      });
      setShowClientModal(true);
    } else {
      // Navigate to emergency bookings dashboard if client not found
      navigate('/emergency-bookings');
    }

    // Remove from active notifications
    setNotifications(prev => prev.filter(n => n.booking.id !== notification.booking.id));
  };

  // Handle Dismiss
  const handleDismiss = (notification: EmergencyNotification) => {
    // Mark booking as seen (removes from notification queue)
    emergencyBookingService.markBookingAsSeen(notification.booking.id);

    // Remove from active notifications
    setNotifications(prev => prev.filter(n => n.booking.id !== notification.booking.id));
  };

  // Get the most recent notification to display
  const activeNotification = notifications.find(n => !n.dismissed);

  if (!activeNotification) {
    return null;
  }

  const { booking, cpiScore } = activeNotification;
  const timeAgo = getTimeAgo(new Date(booking.createdAt));

  return (
    <>
      {/* Main Popup Notification - HIDE when any modal is open */}
      {!showClientModal && !showRejectModal && (
      <div className="emergency-popup-overlay">
        <div className="emergency-popup-container">
          {/* URGENT Badge */}
          <div className="urgent-badge">
            URGENT
          </div>

          {/* Pulsing Logo Container */}
          <div className="logo-container" style={{
            position: 'relative',
            width: '200px',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            {/* Outer Pulsing Rings - Inline styles to ensure they work */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '3px solid #ef4444',
              animation: 'pulseRing 2s ease-out infinite',
              animationDelay: '0s'
            }}></div>
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '3px solid #ef4444',
              animation: 'pulseRing 2s ease-out infinite',
              animationDelay: '0.5s'
            }}></div>
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '3px solid #ef4444',
              animation: 'pulseRing 2s ease-out infinite',
              animationDelay: '1s'
            }}></div>

            {/* Logo Image */}
            <div style={{
              position: 'relative',
              width: '160px',
              height: '160px',
              borderRadius: '50%',
              overflow: 'hidden',
              background: 'white',
              border: '4px solid #ef4444',
              boxShadow: '0 0 0 8px rgba(239, 68, 68, 0.3), 0 0 30px rgba(239, 68, 68, 0.5)',
              animation: 'pulse 2s ease-in-out infinite',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src="./emergency-barber-logo.jpg"
                alt="Emergency Booking"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center top'
                }}
                onError={(e) => {
                  // Fallback to emoji if image fails
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = '<span style="font-size: 60px;">💈</span>';
                }}
              />
            </div>

            {/* CPI Score Badge */}
            <div style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #ef4444, #f97316)',
              color: 'white',
              fontSize: '13px',
              fontWeight: 700,
              padding: '6px 16px',
              borderRadius: '20px',
              border: '3px solid white',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              whiteSpace: 'nowrap',
              zIndex: 10
            }}>
              CPI Score: {cpiScore}%
            </div>
          </div>

          {/* Notification Card */}
          <div className="notification-card">
            <div className="notification-header">
              <Bell className="bell-icon" />
              <span className="emergency-text">Emergency</span>
            </div>

            <p className="notification-message">High-priority request</p>

            {/* Client Info */}
            <div className="client-info">
              <p className="client-name">{booking.clientName}</p>
              <p className="booking-details">
                <Clock className="inline-icon" />
                {booking.requestedTimeSlot} - {timeAgo}
              </p>
              <p className="booking-service">
                <DollarSign className="inline-icon" />
                {booking.serviceType} + ${booking.emergencyFee} fee
              </p>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                className="view-btn"
                onClick={() => handleViewDetails(activeNotification)}
              >
                <Eye className="btn-icon" />
                View
              </button>
              <button
                className="dismiss-btn"
                onClick={() => handleDismiss(activeNotification)}
              >
                Dismiss
              </button>
            </div>
          </div>

          {/* Notification Count Badge */}
          {notifications.length > 1 && (
            <div className="notification-count">
              +{notifications.length - 1} more
            </div>
          )}

          {/* DEV: Clear all button when too many notifications */}
          {notifications.length > 5 && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (window.confirm(`Clear all ${notifications.length} emergency bookings? This will delete them from the database.`)) {
                  await emergencyBookingService.clearAll();
                  setNotifications([]);
                  alert('All emergency bookings cleared!');
                }
              }}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              🗑️ Clear All ({notifications.length})
            </button>
          )}
        </div>
      </div>
      )}

      {/* Client Profile Modal */}
      {showClientModal && selectedClient && (
        <div className="client-modal-overlay" onClick={() => setShowClientModal(false)}>
          <div className="client-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowClientModal(false)}>
              <X />
            </button>

            <div className="modal-header">
              <div className="client-avatar">
                {selectedClient.photo?.startsWith('http') ? (
                  <img src={selectedClient.photo} alt={selectedClient.firstName} />
                ) : (
                  <span className="avatar-initials">
                    {selectedClient.firstName?.[0]}{selectedClient.lastName?.[0]}
                  </span>
                )}
              </div>
              <div className="client-details">
                <h2>{selectedClient.firstName} {selectedClient.lastName}</h2>
                <p className="client-tier">{selectedClient.membershipType || 'Regular'} Member</p>
              </div>
              <div className="cpi-score-large">
                <span className="score">{selectedClient.cpiScore}</span>
                <span className="label">CPI</span>
              </div>
            </div>

            <div className="modal-body">
              {/* Emergency Booking Details - ENHANCED */}
              <div className="emergency-details-section">
                <h3>🚨 Emergency Booking Request</h3>

                {/* PRIMARY INFO - Date & Time (Most Important) */}
                <div className="primary-booking-info" style={{
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  border: '2px solid #f59e0b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Calendar size={24} color="#d97706" />
                    <div>
                      <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 500 }}>REQUESTED DATE</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#78350f' }}>
                        {selectedClient.emergencyBooking?.requestedDate
                          ? new Date(selectedClient.emergencyBooking.requestedDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'Today'}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Clock size={24} color="#d97706" />
                    <div>
                      <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 500 }}>REQUESTED TIME SLOT</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#78350f' }}>
                        {formatTimeSlot(selectedClient.emergencyBooking?.requestedTimeSlot)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Service</span>
                    <span className="value">{selectedClient.emergencyBooking?.serviceType}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Urgency</span>
                    <span className={`value urgency-${selectedClient.emergencyBooking?.urgencyLevel}`} style={{
                      background: selectedClient.emergencyBooking?.urgencyLevel === 'critical' ? '#dc2626' : '#f59e0b',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontWeight: 600
                    }}>
                      {selectedClient.emergencyBooking?.urgencyLevel?.toUpperCase()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Emergency Fee</span>
                    <span className="value" style={{ color: '#059669', fontWeight: 600 }}>
                      +${selectedClient.emergencyBooking?.emergencyFee}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Request Submitted</span>
                    <span className="value">
                      {selectedClient.emergencyBooking?.createdAt
                        ? getTimeAgo(new Date(selectedClient.emergencyBooking.createdAt))
                        : 'Just now'}
                    </span>
                  </div>
                </div>

                {/* Client Average Service Duration */}
                {selectedClient.averageDuration && (
                  <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #86efac',
                    borderRadius: '8px',
                    padding: '12px',
                    marginTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <User size={18} color="#16a34a" />
                    <span style={{ color: '#166534', fontSize: '14px' }}>
                      Client's average service duration: <strong>{selectedClient.averageDuration} minutes</strong>
                    </span>
                  </div>
                )}

                {selectedClient.emergencyBooking?.notes && (
                  <div className="notes-section" style={{
                    background: '#f8fafc',
                    borderRadius: '8px',
                    padding: '12px',
                    marginTop: '12px'
                  }}>
                    <span className="label" style={{ fontSize: '12px', color: '#64748b' }}>Client Notes</span>
                    <p style={{ margin: '4px 0 0', color: '#334155' }}>{selectedClient.emergencyBooking.notes}</p>
                  </div>
                )}
              </div>

              {/* Client Stats */}
              <div className="client-stats">
                <div className="stat">
                  <span className="stat-value">{selectedClient.totalVisits || 0}</span>
                  <span className="stat-label">Visits</span>
                </div>
                <div className="stat">
                  <span className="stat-value">${selectedClient.totalSpent || 0}</span>
                  <span className="stat-label">Spent</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{selectedClient.slikCoins || 0}</span>
                  <span className="stat-label">Coins</span>
                </div>
              </div>

              {/* Contact Info */}
              <div className="contact-section">
                <h3>Contact</h3>
                <p><strong>Phone:</strong> {selectedClient.phone}</p>
                <p><strong>Email:</strong> {selectedClient.email}</p>
              </div>
            </div>

            <div className="modal-actions" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              padding: '16px'
            }}>
              <button
                className="approve-btn"
                onClick={() => {
                  emergencyBookingService.approveBookingSimple(selectedClient.emergencyBooking.id);
                  setShowClientModal(false);
                  setSelectedClient(null);
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'transform 0.2s'
                }}
              >
                <Check size={18} />
                Approve Now
              </button>
              <button
                className="schedule-btn"
                onClick={() => {
                  navigate('/operations', {
                    state: {
                      emergencyClient: selectedClient,
                      openAppointments: true
                    }
                  });
                  setShowClientModal(false);
                }}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Calendar size={18} />
                Schedule Manually
              </button>
              <button
                className="reject-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  console.log('🔶 Propose Alternative Time clicked');

                  // Open the reject with alternative modal
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  setProposedAlternative({
                    date: ymd(tomorrow),
                    time: '10:00',
                    barberId: '',
                    barberName: '',
                    reason: ''
                  });

                  // Close the client modal first, then open reject modal
                  setShowClientModal(false);
                  setShowRejectModal(true);
                  console.log('🔶 showRejectModal set to true');
                }}
                style={{
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  gridColumn: '1 / -1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Send size={18} />
                Propose Alternative Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject with Alternative Modal */}
      {showRejectModal && selectedClient && (
        <div className="client-modal-overlay" onClick={() => setShowRejectModal(false)} style={{ zIndex: 10001 }}>
          <div className="reject-modal" onClick={e => e.stopPropagation()} style={{
            background: 'white',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              color: 'white',
              padding: '20px',
              borderRadius: '16px 16px 0 0'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px' }}>📅 Propose Alternative Time</h2>
                <button
                  onClick={() => setShowRejectModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <X size={18} />
                </button>
              </div>
              <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '14px' }}>
                Can't accommodate the original request? Propose a new time for {selectedClient.firstName}.
              </p>
            </div>

            {/* Original Request Summary */}
            <div style={{
              background: '#fef3c7',
              padding: '16px 20px',
              borderBottom: '1px solid #fde68a'
            }}>
              <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>
                ORIGINAL REQUEST
              </div>
              <div style={{ color: '#78350f' }}>
                <strong>{selectedClient.emergencyBooking?.serviceType}</strong> on{' '}
                <strong>
                  {selectedClient.emergencyBooking?.requestedDate
                    ? new Date(selectedClient.emergencyBooking.requestedDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'Today'}
                </strong>
                {' '}({formatTimeSlot(selectedClient.emergencyBooking?.requestedTimeSlot)})
              </div>
            </div>

            {/* Form */}
            <div style={{ padding: '20px' }}>
              {/* Alternative Date */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Proposed Date *
                </label>
                <input
                  type="date"
                  value={proposedAlternative.date}
                  onChange={e => setProposedAlternative(prev => ({ ...prev, date: e.target.value }))}
                  min={ymd()}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Alternative Time */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Proposed Time *
                </label>
                <input
                  type="time"
                  value={proposedAlternative.time}
                  onChange={e => setProposedAlternative(prev => ({ ...prev, time: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Assigned Barber */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Barber for This Appointment
                </label>
                <select
                  value={proposedAlternative.barberId}
                  onChange={e => {
                    const barber = availableBarbers.find(b => b.id === e.target.value);
                    setProposedAlternative(prev => ({
                      ...prev,
                      barberId: e.target.value,
                      barberName: barber ? `${barber.firstName} ${barber.lastName}` : 'Any Available'
                    }));
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '16px',
                    boxSizing: 'border-box',
                    background: 'white'
                  }}
                >
                  <option value="">Any Available Barber</option>
                  {availableBarbers.map(barber => (
                    <option key={barber.id} value={barber.id}>
                      {barber.firstName} {barber.lastName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reason / Message to Client */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Message to Client (Optional)
                </label>
                <textarea
                  value={proposedAlternative.reason}
                  onChange={e => setProposedAlternative(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Sorry, we're fully booked at that time. How about..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Preview of what will be sent */}
              {proposedAlternative.date && proposedAlternative.time && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '10px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '12px', color: '#166534', fontWeight: 600, marginBottom: '8px' }}>
                    ✉️ CLIENT WILL RECEIVE:
                  </div>
                  <div style={{ color: '#15803d', fontSize: '14px' }}>
                    "We can see you on <strong>
                      {new Date(proposedAlternative.date + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </strong> at <strong>{formatTime12Hour(proposedAlternative.time)}</strong>
                    {proposedAlternative.barberName && ` with ${proposedAlternative.barberName}`}.
                    {proposedAlternative.reason && ` ${proposedAlternative.reason}`}"
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowRejectModal(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    background: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  Cancel
                </button>
                <button
                  disabled={isSending}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    // Immediate visual feedback
                    setIsSending(true);
                    alert('Button clicked! Processing...');

                    console.log('🔘 Send to Client button clicked');
                    console.log('Proposed alternative:', proposedAlternative);
                    console.log('Selected client:', selectedClient);

                    if (!proposedAlternative.date || !proposedAlternative.time) {
                      alert('Please select a date and time');
                      setIsSending(false);
                      return;
                    }

                    if (!selectedClient?.emergencyBooking?.id) {
                      alert('Error: No booking ID found');
                      console.error('Missing booking ID:', selectedClient);
                      setIsSending(false);
                      return;
                    }

                    try {
                      // Create the alternative slot. `new Date(date + 'T' + time)` goes Invalid on a
                      // 12-hour time ("5:30 PM") → the proposed date would be garbage. parseYmd (local day)
                      // + parseTimeToMinutes (12h/24h). (Jose 2026-10-23)
                      const alternativeDate = parseYmd(proposedAlternative.date);
                      const _altMins = parseTimeToMinutes(proposedAlternative.time) ?? 0;
                      alternativeDate.setHours(Math.floor(_altMins / 60), _altMins % 60, 0, 0);
                      console.log('📅 Alternative date:', alternativeDate);

                      // Call the service to reject with alternative
                      console.log('📤 Calling rejectWithAlternative...');
                      const result = await emergencyBookingService.rejectWithAlternative(
                        selectedClient.emergencyBooking.id,
                        {
                          proposedDate: alternativeDate,
                          proposedTime: proposedAlternative.time,
                          proposedBarber: proposedAlternative.barberName || 'Any Available',
                          proposedBarberId: proposedAlternative.barberId,
                          message: proposedAlternative.reason
                        }
                      );

                      console.log('✅ rejectWithAlternative result:', result);
                      alert('Counter offer sent! The client will receive an SMS notification.');

                      // Close modals
                      setShowRejectModal(false);
                      setShowClientModal(false);
                      setSelectedClient(null);

                      // Reset form
                      setProposedAlternative({
                        date: '',
                        time: '',
                        barberId: '',
                        barberName: '',
                        reason: ''
                      });
                    } catch (error) {
                      console.error('❌ Error sending counter offer:', error);
                      alert('Error sending counter offer: ' + (error as Error).message);
                    } finally {
                      setIsSending(false);
                    }
                  }}
                  style={{
                    flex: 2,
                    padding: '14px',
                    border: 'none',
                    borderRadius: '10px',
                    background: isSending
                      ? 'linear-gradient(135deg, #9ca3af, #6b7280)'
                      : 'linear-gradient(135deg, #f97316, #ea580c)',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: isSending ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isSending ? 0.7 : 1
                  }}
                >
                  <Send size={18} />
                  {isSending ? 'Sending...' : 'Send to Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to format time slot
function formatTimeSlot(slot: string | undefined): string {
  if (!slot) return 'Any Time';
  const slots: Record<string, string> = {
    morning: 'Morning (9 AM - 12 PM)',
    afternoon: 'Afternoon (12 PM - 3 PM)',
    evening: 'Evening (3 PM - 6 PM)',
    asap: 'ASAP - Next Available'
  };
  return slots[slot] || slot;
}

// Helper function to format time to 12-hour format
function formatTime12Hour(time24: string): string {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Helper function to get time ago string
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString();
}

export default EmergencyBookingPopupNotification;
